import { query_dispatcher, sync_command_dispatcher } from '@/api/client';
import { Nomina, NominaSummaryEmpleadoCreationResult, isSummaryNomina } from '../domain/Nomina';
import { NominaFieldLocation } from '../domain/fieldHighlight';
import { NominaEntryMove, sortMovesByKind } from '../domain/nominaEntry';
import {
  DatSyncCheckResult,
  NominaAccountResult,
  NominaIngestResult,
  NominaListFilterInput,
  NominaListResult,
  NominaProcessResult,
  NominaMassiveChangeInput,
  NominaMassiveChangeResult,
  NominaRepository,
  NominaStateLogEntry,
  NominaStateSnapshot,
  NominaRetryParseResult,
  NominaAcceptSplitResult,
  NominaStateCounts,
  NominaCompanySettings,
  NominaCompanySettingsResult,
} from '../domain/NominaRepository';

export const RESTORE_FAILED_MESSAGE = 'No se pudieron restaurar las nóminas.';
export const COMPANY_SETTINGS_LOAD_FAILED_MESSAGE = 'No se pudo cargar la configuración de nóminas.';
export const COMPANY_SETTINGS_SAVE_FAILED_MESSAGE = 'No se pudo guardar la configuración de nóminas.';
export const SUMMARY_EMPLEADOS_CREATE_FAILED_MESSAGE = 'No se pudo dar de alta a los empleados del resumen.';
export const ACCEPT_SPLIT_FAILED_MESSAGE = 'No se pudo aceptar la separación. Inténtelo de nuevo.';
export const CHECK_DAT_SYNC_FAILED_MESSAGE = 'No se pudo comprobar si el programa contable ha importado el fichero.';

export class ApiNominaRepository implements NominaRepository {
  async list(companyId: number, filters: NominaListFilterInput[] = [], limit = null, offset = null,
             sortBy = null, sortDesc = false, withCounts = false): Promise<NominaListResult> {
    const params: any = {
      company_id: companyId,
      filters,
    };
    if (limit !== null) params.limit = limit;
    if (offset !== null) params.offset = offset;
    if (sortBy !== null) { params.sort_by = sortBy; params.sort_desc = sortDesc; }
    // Solo se manda cuando hace falta: la query lo trata como opcional y así las
    // demás pantallas no cargan con el coste de los contadores.
    if (withCounts) params.with_counts = true;
    const res: any = await query_dispatcher.dispatch('nomina.list', params);
    const items = ((res && res.items) || []).map((item: any) => this.toDomain(item));
    const result: NominaListResult = { items, total_count: (res && res.total_count) || 0 };
    if (withCounts) {
      result.counts = (res && res.counts) || {};
      result.counts_total = (res && res.counts_total) || 0;
    }
    return result;
  }

  async listDeleted(companyId: number, filters: NominaListFilterInput[] = []): Promise<NominaListResult> {
    const res: any = await query_dispatcher.dispatch('nomina.list_deleted', {
      company_id: companyId,
      filters,
    });
    const items = ((res && res.items) || []).map((item: any) => this.toDomain(item));
    return { items, total_count: (res && res.total_count) || 0 };
  }

  async restore(uuidList: string[], companyId: number): Promise<number> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.restore', {
      uuid_list: uuidList,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || RESTORE_FAILED_MESSAGE);
    }
    return out.restored_count || 0;
  }

  async getById(uuid: string, companyId: number): Promise<Nomina | null> {
    const res: any = await query_dispatcher.dispatch('nomina.get', {
      uuid,
      company_id: companyId,
    });
    if (!res || res.status === 'error') return null;
    return this.toDomain(res);
  }

  async remove(uuidList: string[], companyId: number): Promise<void> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.delete', {
      uuid_list: uuidList,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (out && out.status === 'error') throw new Error(out.message || 'Nomina delete failed');
  }

  async removeAll(companyId: number, filters: NominaListFilterInput[] = []): Promise<void> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.delete', {
      delete_all: true,
      company_id: companyId,
      filters,
    });
    const out = (res && res.output) || res;
    if (out && out.status === 'error') throw new Error(out.message || 'Nomina delete failed');
  }

  async applyMassiveChange(
    params: NominaMassiveChangeInput, companyId: number,
  ): Promise<NominaMassiveChangeResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.massive_change', {
      company_id: companyId,
      field: params.field,
      value: params.value,
      uuid_list: params.uuid_list || [],
      apply_to_all: Boolean(params.apply_to_all),
      filters: params.filters || [],
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') throw new Error((out && out.message) || 'Massive change failed');
    return {
      updated_count: out.updated_count || 0,
      skipped_count: out.skipped_count || 0,
      deleted_entry_count: out.deleted_entry_count || 0,
      detached_entry_count: out.detached_entry_count || 0,
      message: out.message || '',
    };
  }

  async save(nomina: Nomina, companyId: number): Promise<string> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.save', {
      uuid: nomina.uuid,
      company_id: companyId,
      // Se manda siempre, también vacío: '' significa "desenlazar" y el backend
      // lo distingue de no mandarlo (ver `empleado_uuid` en SaveNominaCommand).
      empleado_uuid: nomina.empleado_uuid || '',
      employee_name: nomina.employee_name,
      employee_dni: nomina.employee_dni,
      period: nomina.period,
      payment_date: nomina.payment_date,
      gross_amount: nomina.gross_amount,
      irpf_amount: nomina.irpf_amount,
      ss_employee_amount: nomina.ss_employee_amount,
      other_deductions_amount: nomina.other_deductions_amount || 0,
      in_kind_amount: nomina.in_kind_amount || 0,
      deduction_in_kind_amount: nomina.deduction_in_kind_amount || 0,
      deduction_advance_amount: nomina.deduction_advance_amount || 0,
      deduction_staff_loan_amount: nomina.deduction_staff_loan_amount || 0,
      deduction_garnishment_amount: nomina.deduction_garnishment_amount || 0,
      deduction_union_fee_amount: nomina.deduction_union_fee_amount || 0,
      deductions_detail: nomina.deductions_detail || '',
      irpf_rate: nomina.irpf_rate || 0,
      irpf_detail: nomina.irpf_detail || '',
      ss_employee_line_map: nomina.ss_employee_line_map || {},
      ss_employer_line_map: nomina.ss_employer_line_map || {},
      ss_employee_detail: nomina.ss_employee_detail || '',
      ss_employer_detail: nomina.ss_employer_detail || '',
      ss_bodies_detail: nomina.ss_bodies_detail || '',
      entry_mode: nomina.entry_mode || '',
      accounting_method: nomina.accounting_method || '',
      gross_detail: nomina.gross_detail || '',
      // Solo en un resumen: en una particular no significa nada y no se toca.
      ...(isSummaryNomina(nomina) ? { summary_net_detail: nomina.summary_net_detail || '' } : {}),
      ss_employer_amount: nomina.ss_employer_amount,
      net_amount: nomina.net_amount,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') throw new Error((out && out.message) || 'Nomina save failed');
    const savedUuid = out.uuid || nomina.uuid;
    nomina.uuid = savedUuid;
    return savedUuid;
  }

  async stateCounts(companyId: number): Promise<NominaStateCounts> {
    const res: any = await query_dispatcher.dispatch('nomina.state_counts', {
      company_id: companyId,
    });
    return {
      counts: (res && res.counts) || {},
      total_count: (res && res.total_count) || 0,
    };
  }

  async creditBalance(companyId: number): Promise<number> {
    const res: any = await query_dispatcher.dispatch('nomina.credit_balance', { company_id: companyId });
    if (!res || res.status === 'error') throw new Error('Nomina credit balance failed');
    return Number(res.available_credits) || 0;
  }

  async uploadAndIngest(frontPath: string, filename: string, companyId: number,
    splitMode = '', nominaKind = ''): Promise<NominaIngestResult> {
    const params: any = { company_id: companyId, front_path: frontPath, filename };
    // Solo si se eligió: sin él la subida es la de siempre, una nómina por PDF.
    if (splitMode) params.split_mode = splitMode;
    if (nominaKind) params.nomina_kind = nominaKind;
    const res: any = await sync_command_dispatcher.dispatch('nomina.ingest', params);
    const out = (res && res.output) || res;
    // El backend puede crear la nómina Y devolver error: sin créditos la deja en
    // «Errores» con el motivo. Si no se mira el status, la bandeja de subida la daría
    // por buena y el usuario no sabría que tiene que recargar para que se lea.
    if (out && out.status === 'error') {
      throw new Error(out.message || 'No se pudo subir la nómina.');
    }
    return {
      uuid: (out && out.uuid) || '',
      state: (out && out.state) || 'processing',
    };
  }

  async generateEntry(uuid: string, companyId: number): Promise<NominaAccountResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.generate_entry', {
      uuid,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || 'Nomina generate entry failed');
    }
    return {
      uuid: out.uuid || uuid,
      state: out.state || '',
      entry_reference: out.entry_reference || '',
    };
  }

  async account(uuid: string, companyId: number): Promise<NominaAccountResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.account', {
      uuid,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') throw new Error((out && out.message) || 'Nomina account failed');
    return {
      uuid: out.uuid || uuid,
      state: out.state || '',
      entry_reference: out.entry_reference || '',
    };
  }

  async process(uuidList: string[], companyId: number): Promise<NominaProcessResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.process', {
      uuid_list: uuidList,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || 'No se pudieron procesar las nóminas.');
    }
    return {
      processedCount: out.processed_count || 0,
      noCreditsCount: out.no_credits_count || 0,
      missingCredits: out.missing_credits || 0,
    };
  }

  async acceptSplit(uuid: string, companyId: number, rangeList: number[][]): Promise<NominaAcceptSplitResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.accept_split', {
      uuid,
      company_id: companyId,
      range_list: rangeList,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || ACCEPT_SPLIT_FAILED_MESSAGE);
    }
    return {
      uuidList: out.uuid_list || [],
      partCount: out.part_count || 0,
    };
  }

  async retryParse(uuidList: string[], companyId: number): Promise<NominaRetryParseResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.retry_parse', {
      uuid_list: uuidList,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || 'Nomina retry parse failed');
    }
    return {
      retriedCount: out.retried_count || 0,
      skippedCount: out.skipped_count || 0,
      deletedEntryCount: out.deleted_entry_count || 0,
      failedCount: out.failed_count || 0,
    };
  }

  async setPdfRotation(uuid: string, companyId: number, rotation: number): Promise<number> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.set_pdf_rotation', {
      uuid,
      company_id: companyId,
      rotation,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || 'Nomina set pdf rotation failed');
    }
    return out.pdf_rotation || 0;
  }

  /**
   * Pregunta al programa contable si ya importó el fichero .DAT de esta nómina.
   *
   * Comando y no query aunque parezca una consulta: encola trabajo para el conector
   * y, si la respuesta es que sí, cierra la nómina.
   */
  async checkDatSync(
    uuid: string, companyId: number, canRequest = false,
  ): Promise<DatSyncCheckResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.check_dat_sync', {
      uuid,
      company_id: companyId,
      can_request: canRequest,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || CHECK_DAT_SYNC_FAILED_MESSAGE);
    }
    return {
      isSynced: out.is_synced === true,
      message: out.message || '',
    };
  }

  /**
   * Los asientos de la nómina, leídos de la contabilidad.
   *
   * Query y no comando: solo lee. Devuelve [] cuando la nómina aún no tiene
   * asiento, que es un estado normal (en revisión) y no un error.
   */
  async getEntry(uuid: string, companyId: number): Promise<NominaEntryMove[]> {
    const res: any = await query_dispatcher.dispatch('nomina.get_entry', {
      uuid,
      company_id: companyId,
    });
    if (!res || res.status === 'error') return [];
    return sortMovesByKind((res.move_list || []).map((raw: any) => ({
      kind: raw.kind || '',
      description: raw.description || '',
      total_debit: Number(raw.total_debit) || 0,
      total_credit: Number(raw.total_credit) || 0,
      is_balanced: raw.is_balanced !== false,
      erp_state: raw.erp_state || '',
      erp_move_number: raw.erp_move_number || '',
      erp_error: raw.erp_error || '',
      line_list: (raw.line_list || []).map((line: any) => ({
        account: line.account || '',
        description: line.description || '',
        debit: Number(line.debit) || 0,
        credit: Number(line.credit) || 0,
      })),
    })));
  }

  /**
   * Guarda el asiento editado. Solo viajan los campos que el usuario puede tocar:
   * los totales los recalcula el dominio y el cuadre lo valida el backend.
   */
  async saveEntry(uuid: string, companyId: number, moveList: NominaEntryMove[]): Promise<void> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.save_entry', {
      uuid,
      company_id: companyId,
      move_list: moveList.map(move => ({
        kind: move.kind,
        description: move.description,
        line_list: move.line_list.map(line => ({
          account: line.account,
          description: line.description,
          debit: line.debit,
          credit: line.credit,
        })),
      })),
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || 'Nomina save entry failed');
    }
  }

  /**
   * Dónde está en el PDF cada dato leído, para resaltarlo.
   *
   * Es un comando y no una query porque el servidor guarda el resultado: la
   * primera llamada hace OCR (de pago, por página) y las siguientes devuelven lo
   * guardado. Ver `LocateNominaFieldsCommand`.
   */
  async locateFields(uuid: string, companyId: number): Promise<NominaFieldLocation[]> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.locate_fields', {
      uuid,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || 'Nomina locate fields failed');
    }
    return ((out.field_location_list) || []).map((raw: any) => ({
      field: raw.field || '',
      color: raw.color || '',
      x: Number(raw.x) || 0,
      y: Number(raw.y) || 0,
      width: Number(raw.width) || 0,
      height: Number(raw.height) || 0,
      page: Number(raw.page) || 0,
      parent_field: raw.parent_field || '',
    }));
  }

  async getState(uuid: string, companyId: number): Promise<NominaStateSnapshot | null> {
    const res: any = await query_dispatcher.dispatch('nomina.get_state', {
      uuid,
      company_id: companyId,
    });
    if (!res || res.status === 'error') return null;
    return {
      state: res.state || '',
      error_message: res.error_message || '',
      entry_reference: res.entry_reference || '',
    };
  }

  async stateHistory(uuid: string, companyId: number): Promise<NominaStateLogEntry[]> {
    const res: any = await query_dispatcher.dispatch('nomina.state_history', {
      uuid,
      company_id: companyId,
    });
    // Un error SÍ se propaga (no se devuelve lista vacía): el popup tiene que poder
    // distinguir "esta nómina no tiene historia" de "no he podido preguntarlo", que es
    // justo lo que se está investigando cuando alguien lo abre.
    if (!res || res.status === 'error') {
      throw new Error((res && res.message) || 'No se pudo leer el histórico de estados.');
    }
    return (res.items || []).map((item: any) => ({
      nomina_uuid: item.nomina_uuid || '',
      from_state: item.from_state || '',
      to_state: item.to_state || '',
      created: item.created || '',
      source: item.source || '',
      source_label: item.source_label || '',
      // `|| null` y no `|| 0`: cero sería el usuario cero, y aquí "nadie" es un dato.
      actor_user_id: item.actor_user_id === undefined ? null : item.actor_user_id,
      error_message: item.error_message || '',
    }));
  }

  async syncErpResult(uuid: string, companyId: number): Promise<void> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.sync_erp_result', {
      uuid,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    // Un `error` aquí es "no se pudo leer la respuesta del conector", no un fallo de la
    // nómina: se propaga para que el sondeo lo registre, pero él sigue consultando el
    // estado igual — perder una vuelta es mejor que dejar de vigilar.
    if (out && out.status === 'error') {
      throw new Error(out.message || 'No se pudo sincronizar el asiento.');
    }
  }

  async recover(uuid: string, companyId: number): Promise<void> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.recover', {
      uuid,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      // El backend explica QUÉ falta: se propaga tal cual, que es lo que el usuario
      // necesita para corregirlo.
      throw new Error((out && out.message) || 'No se pudo validar la nómina.');
    }
  }

  async getPdfUrl(path: string): Promise<string> {
    const res: any = await query_dispatcher.dispatch('file.get.temp.url', { path });
    return (res && res.url) || '';
  }

  async summaryUploadDefault(companyId: number): Promise<boolean> {
    const res: any = await query_dispatcher.dispatch('nomina.inherited_settings', {
      company_id: companyId,
    });
    return Boolean(res && res.summary_upload_default);
  }

  async createSummaryEmpleados(uuid: string, companyId: number): Promise<NominaSummaryEmpleadoCreationResult> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.create_summary_empleados', {
      uuid,
      company_id: companyId,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      throw new Error((out && out.message) || SUMMARY_EMPLEADOS_CREATE_FAILED_MESSAGE);
    }
    return {
      createdCount: out.created_count || 0,
      missingCount: out.missing_count || 0,
      state: out.state || '',
      errorMessage: out.error_message || '',
      rejectedList: (out.rejected_list || []).map((item: any) => ({
        label: item.label || '',
        reason: item.reason || '',
      })),
    };
  }

  async companySettings(companyId: number): Promise<NominaCompanySettingsResult> {
    const res: any = await query_dispatcher.dispatch('nomina.company_settings', {
      company_id: companyId,
    });
    if (!res || res.status === 'error') {
      throw new Error((res && res.message) || COMPANY_SETTINGS_LOAD_FAILED_MESSAGE);
    }
    return { settingMap: res.settings || {}, isEditable: Boolean(res.can_edit) };
  }

  async saveCompanySettings(companyId: number, settingMap: NominaCompanySettings): Promise<NominaCompanySettings> {
    const res: any = await sync_command_dispatcher.dispatch('nomina.save_company_settings', {
      company_id: companyId,
      settings: settingMap,
    });
    const out = (res && res.output) || res;
    if (!out || out.status === 'error') {
      // El backend dice qué falla (una cuenta que no está en el maestro, sin permiso).
      throw new Error((out && out.message) || COMPANY_SETTINGS_SAVE_FAILED_MESSAGE);
    }
    return out.settings || {};
  }

  toDomain(raw: any): Nomina {
    const body: any = raw || {};
    return {
      uuid: body.uuid || '',
      created_at: body.created_at || '',
      nomina_kind: body.nomina_kind || '',
      summary_header_map: (body.summary_header_map || []).map((mapping: any) => ({
        header: mapping.header || '',
        field: mapping.field || '',
      })),
      summary_employee_net_list: (body.summary_employee_net_list || []).map((item: any) => ({
        employee_code: item.employee_code || '',
        employee_name: item.employee_name || '',
        employee_dni: item.employee_dni || '',
        net_amount: item.net_amount || 0,
        empleado_uuid: item.empleado_uuid || '',
      })),
      summary_net_detail: body.summary_net_detail || '',
      source_file_type: body.source_file_type || '',
      empleado_uuid: body.empleado_uuid || '',
      employee_name: body.employee_name || '',
      employee_dni: body.employee_dni || '',
      period: body.period || '',
      payment_date: body.payment_date || '',
      gross_amount: body.gross_amount || 0,
      net_amount: body.net_amount || 0,
      irpf_amount: body.irpf_amount || 0,
      ss_employee_amount: body.ss_employee_amount || 0,
      other_deductions_amount: body.other_deductions_amount || 0,
      in_kind_amount: body.in_kind_amount || 0,
      deduction_in_kind_amount: body.deduction_in_kind_amount || 0,
      deduction_advance_amount: body.deduction_advance_amount || 0,
      deduction_staff_loan_amount: body.deduction_staff_loan_amount || 0,
      deduction_garnishment_amount: body.deduction_garnishment_amount || 0,
      deduction_union_fee_amount: body.deduction_union_fee_amount || 0,
      deductions_detail: body.deductions_detail || '',
      irpf_rate: body.irpf_rate || 0,
      irpf_detail: body.irpf_detail || '',
      ss_employee_line_map: body.ss_employee_line_map || {},
      ss_employer_line_map: body.ss_employer_line_map || {},
      ss_employee_detail: body.ss_employee_detail || '',
      ss_employer_detail: body.ss_employer_detail || '',
      ss_bodies_detail: body.ss_bodies_detail || '',
      entry_mode: body.entry_mode || '',
      accounting_method: body.accounting_method || '',
      gross_detail: body.gross_detail || '',
      computed_field_list: body.computed_field_list || [],
      employer_contribution: body.employer_contribution || {},
      employee_contribution: body.employee_contribution || {},
      ss_employer_amount: body.ss_employer_amount || 0,
      employee_hire_date: body.employee_hire_date || '',
      employee_job_title: body.employee_job_title || '',
      employee_email: body.employee_email || '',
      employee_phone: body.employee_phone || '',
      ai_read_snapshot: body.ai_read_snapshot || {},
      generated_entry_snapshot: body.generated_entry_snapshot || [],
      // Sin default: un `state` ausente NO es 'processing'. Ese default hacía
      // que una fila con estado desconocido se pintara como "la IA la está
      // leyendo" indefinidamente (el aviso no se iba ni tras F5).
      state: body.state || '',
      pdf_path: body.pdf_path || '',
      pdf_url: body.pdf_url || '',
      source_filename: body.source_filename || '',
      error_message: body.error_message || '',
      entry_reference: body.entry_reference || '',
      erp_registration_reference: body.erp_registration_reference || '',
      erp_payment_reference: body.erp_payment_reference || '',
      pdf_rotation: body.pdf_rotation || 0,
      is_entry_edited: Boolean(body.is_entry_edited),
      // Separador inteligente: sin estos, la pantalla no sabía que la nómina estaba
      // separando (decía «La IA está leyendo») ni qué cortes revisar («Sin propuesta»).
      split_session_list: (body.split_session_list || []).map((session: any) => ({
        session_id: session.session_id || '',
        started_at: session.started_at || '',
      })),
      split_range_list: (body.split_range_list || []).map((splitRange: any) => [splitRange[0], splitRange[1]]),
      split_error: body.split_error && body.split_error.error_code
        ? {
          error_code: body.split_error.error_code,
          refunded: Boolean(body.split_error.refunded),
          refunded_credits: body.split_error.refunded_credits || 0,
          at: body.split_error.at || '',
        }
        : null,
    };
  }
}
